import { useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import type { InputHTMLAttributes } from 'react'
import { cn } from '@/lib/utils'

const inputBaseClassName =
  'w-full rounded-lg border border-[#858C94] bg-white px-4 py-3.5 pr-10 text-base disabled:cursor-not-allowed disabled:opacity-50'

const labelClassName = 'mb-2 block text-sm font-semibold text-black'

const toggleButtonClassName =
  'text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2 rounded p-1 transition-colors'

export interface FormPasswordInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'className' | 'type'
> {
  id: string
  label: string
  required?: boolean
  showPasswordLabel: string
  hidePasswordLabel: string
  hint?: React.ReactNode
  className?: string
  inputClassName?: string
}

export function FormPasswordInput({
  id,
  label,
  required = false,
  showPasswordLabel,
  hidePasswordLabel,
  hint,
  className,
  inputClassName: inputClassNameProp,
  ...inputProps
}: FormPasswordInputProps) {
  const [showPassword, setShowPassword] = useState(false)

  return (
    <div className={className}>
      <label htmlFor={id} className={labelClassName}>
        {label}
        {required && <span className="text-primary"> *</span>}
      </label>
      <div className="relative">
        <input
          id={id}
          type={showPassword ? 'text' : 'password'}
          className={cn(inputBaseClassName, inputClassNameProp)}
          required={required}
          {...inputProps}
        />
        <button
          type="button"
          onClick={() => setShowPassword(prev => !prev)}
          className={toggleButtonClassName}
          aria-label={showPassword ? hidePasswordLabel : showPasswordLabel}
          tabIndex={-1}
        >
          {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
        </button>
      </div>
      {hint && <p className="mt-1 text-sm font-normal text-[#717171]">{hint}</p>}
    </div>
  )
}
